/**
 * Pure filtering and ranking for `CardSearchPanel`.
 */
import type { MinimalCard } from '@/services/cardDatabase';
import type { Color } from '@/types/card';

export type CardSearchType = 'creature' | 'instant' | 'sorcery' | 'artifact' | 'enchantment' | 'planeswalker' | 'land' | 'token';

export interface CardSearchFilterState {
  types: Set<CardSearchType>;
  colors: Set<Color>;
  /** Matches cards with an empty colour identity, OR-ed with any selected colours. */
  includeColorless: boolean;
  cmcMin: number | null;
  cmcMax: number | null;
}

export const CARD_SEARCH_TYPES: { value: CardSearchType; label: string }[] = [
  { value: 'creature', label: 'Creature' },
  { value: 'instant', label: 'Instant' },
  { value: 'sorcery', label: 'Sorcery' },
  { value: 'artifact', label: 'Artifact' },
  { value: 'enchantment', label: 'Enchantment' },
  { value: 'planeswalker', label: 'Planeswalker' },
  { value: 'land', label: 'Land' },
  { value: 'token', label: 'Token' },
];

export const CARD_SEARCH_COLORS: { value: Color; label: string; symbol: string }[] = [
  { value: 'W', label: 'White', symbol: 'w' },
  { value: 'U', label: 'Blue', symbol: 'u' },
  { value: 'B', label: 'Black', symbol: 'b' },
  { value: 'R', label: 'Red', symbol: 'r' },
  { value: 'G', label: 'Green', symbol: 'g' },
];

export const CARD_SEARCH_CMC_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];

export const EMPTY_CARD_SEARCH_FILTERS: CardSearchFilterState = {
  types: new Set(),
  colors: new Set(),
  includeColorless: false,
  cmcMin: null,
  cmcMax: null,
};

export function hasActiveCardSearchFilters(filters: CardSearchFilterState): boolean {
  return (
    filters.types.size > 0 || filters.colors.size > 0 || filters.includeColorless || filters.cmcMin !== null || filters.cmcMax !== null
  );
}

/** Legality format keys are lowercase deck format names; null skips the legality check. */
export function isLegalInFormat(card: MinimalCard, format: string | null | undefined): boolean {
  if (!format || !card.legalities) return true;
  // Tokens report `not_legal` everywhere but are still valid deck contents.
  if (card.type_line?.toLowerCase().includes('token')) return true;

  const legality = card.legalities[format.toLowerCase() as keyof typeof card.legalities];
  return !legality || legality === 'legal' || legality === 'restricted';
}

export function matchesCardSearchFilters(card: MinimalCard, filters: CardSearchFilterState): boolean {
  if (filters.types.size > 0 && card.type_line) {
    const typeLine = card.type_line.toLowerCase();
    if (!Array.from(filters.types).some((type) => typeLine.includes(type))) return false;
  }

  if (filters.colors.size > 0 || filters.includeColorless) {
    const isColorless = card.color_identity.length === 0;
    const matchesColor = isColorless ? filters.includeColorless : card.color_identity.some((c) => filters.colors.has(c));
    if (!matchesColor) return false;
  }

  if (filters.cmcMin !== null && card.cmc != null && card.cmc < filters.cmcMin) return false;
  if (filters.cmcMax !== null && card.cmc != null && card.cmc > filters.cmcMax) return false;

  return true;
}

/** Owned cards first, then alphabetical. */
export function compareByOwnedThenName(a: MinimalCard, b: MinimalCard, ownedNames: Set<string>): number {
  const aOwned = ownedNames.has(a.name_lower);
  const bOwned = ownedNames.has(b.name_lower);
  if (aOwned !== bOwned) return aOwned ? -1 : 1;
  return a.name.localeCompare(b.name);
}
